// useBaseAccess.js

import { useEffect, useMemo, useState } from 'react';

import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import { canAccessAction, BASE_ROLES } from 'security/rbac';
import { subscribeToBaseAccess } from 'services/bases/BaseRoleService';

export default function useBaseAccess(baseOverride = null) {
  const { user } = useUser();
  const { activeBase } = useBase();

  const effectiveBase = baseOverride || activeBase || null;

  const [access, setAccess] = useState(null);
  const [isRoleLoading, setIsRoleLoading] = useState(true);

  const baseKey = effectiveBase?.key || null;
  const userKey = user?.key || null;

  useEffect(() => {
    if (!baseKey || !userKey) {
      setAccess(null);
      setIsRoleLoading(false);
      return undefined;
    }

    if (effectiveBase?.ownerKey === userKey) {
      setAccess({
        roleKey: BASE_ROLES.OWNER,
        tier: BASE_ROLES.OWNER,
        isCustom: false,
        roleDefinition: null,
        roleMissing: false,
      });
      setIsRoleLoading(false);
      return undefined;
    }

    setIsRoleLoading(true);

    const unsubscribe = subscribeToBaseAccess({
      baseKey,
      userKey,
      base: effectiveBase,
      callback: (nextAccess) => {
        setAccess(nextAccess || null);
        setIsRoleLoading(false);
      },
      onError: () => {
        setAccess(null);
        setIsRoleLoading(false);
      },
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [effectiveBase, userKey, baseKey]);

  return useMemo(() => {
    const baseRole = access?.roleKey || null;
    const baseTier = access?.tier || null;

    return {
      baseRole,
      baseTier,
      isCustomRole: access?.isCustom === true,
      roleMissing: access?.roleMissing === true,
      roleDefinition: access?.roleDefinition || null,
      isRoleLoading,
      can: (action) => canAccessAction({
        tier: baseTier,
        isCustom: access?.isCustom === true,
        roleDefinition: access?.roleDefinition || null,
        roleMissing: access?.roleMissing === true,
        action,
      }),
    };
  }, [access, isRoleLoading]);
}
